import React from 'react';
import styled from 'styled-components';
import { theme } from '../theme';
import logo from '../assets/logo.jpeg';
import hidevLogo from '../assets/hidev_logo.png';

const FooterContainer = styled.footer`
  background-color: rgba(61, 54, 53, 0.98);
  color: ${theme.colors.light};
  padding: 50px 0 40px;
  border-top: 1px solid ${theme.colors.burgundy};
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(to right, transparent, ${theme.colors.caramel}, transparent);
    opacity: 0.6;
  }
  
  @media (max-width: 480px) {
    padding: 40px 0 30px;
  }
`;

const FooterContent = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 25px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 35px;
    text-align: center;
  }
  
  @media (max-width: 480px) {
    padding: 0 15px;
    gap: 30px;
  }
`;

const LogoContainer = styled.div`
  img {
    max-height: 85px;
    border-radius: ${theme.borderRadius.small};
    transition: transform 0.3s ease, filter 0.3s ease;
    filter: brightness(1.05) contrast(1.05);
    
    &:hover {
      transform: scale(1.03);
      filter: brightness(1.1) contrast(1.1);
    }
    
    @media (max-width: 480px) {
      max-height: 75px;
    }
  }
`;

const NavLinks = styled.nav`
  display: flex;
  gap: 35px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: center;
    gap: 20px;
  }
  
  @media (max-width: 480px) {
    gap: 15px;
  }
`;

const NavLink = styled.a`
  color: ${theme.colors.light};
  text-decoration: none;
  font-family: ${theme.fonts.accent};
  font-weight: 500;
  font-size: 13px;
  letter-spacing: 2px;
  text-transform: uppercase;
  transition: color 0.3s ease;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 1px;
    bottom: -3px;
    left: 0;
    background-color: ${theme.colors.caramel};
    transition: width 0.3s ease;
  }

  &:hover {
    color: ${theme.colors.caramel};

    &:after {
      width: 100%;
    }
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    letter-spacing: 1.5px;
  }
`;

const HiDevLogo = styled.div`
  img {
    max-height: 55px;
    opacity: 0.85;
    transition: all 0.3s ease;
    background-color: ${theme.colors.light};
    padding: 4px;
    border-radius: ${theme.borderRadius.small};
    
    &:hover {
      opacity: 1;
      transform: scale(1.03);
    }
    
    @media (max-width: 480px) {
      max-height: 50px;
    }
  }
`;

const Copyright = styled.div`
  text-align: center;
  margin-top: 35px;
  font-family: ${theme.fonts.body};
  font-size: 14px;
  color: ${theme.colors.taupe};
  font-style: italic;
  
  @media (max-width: 480px) {
    font-size: 13px;
    margin-top: 25px;
  }
`;

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <FooterContainer>
      <FooterContent>
        <LogoContainer>
          <img src={logo} alt="Fiori Italian Bistro Logo" />
        </LogoContainer>

        <NavLinks>
          <NavLink href="#home">Home</NavLink>
          <NavLink href="#menu">Menu</NavLink>
          <NavLink href="#gallery">Gallery</NavLink>
          <NavLink href="#contact">Contact</NavLink>
        </NavLinks>

        <HiDevLogo>
          <a href="https://hidevmobile.com" target="_blank" rel="noopener noreferrer">
            <img src={hidevLogo} alt="HiDev Logo" />
          </a>
        </HiDevLogo>
      </FooterContent>

      <Copyright>
        © {currentYear} Fiori Italian Bistro. All rights reserved.
      </Copyright>
    </FooterContainer>
  );
};

export default Footer; 